//Import React
import React, { useContext, useMemo } from "react";
//Imports Contexts
import { AppContext } from "../../../../contexts/AppContext";
import { AuthContext } from "../../../../contexts/AuthContext";
//Imports Components
import ComplianceIndicator from "../../../../shared/ComplianceReport";

function PMEComplianceSummary() {
    const { pmeList, establishmentList } = useContext(AppContext);
    const { userData } = useContext(AuthContext);

    const calculateTotals = (activities) => {
        return {
            totalSubsidyAmount: activities.reduce((sum, activity) => sum + (activity.totalSubsidyAmount || 0), 0),
            totalAccruedExpense: activities.reduce((sum, activity) => sum + (activity.accruedExpense || 0), 0),
            totalEffectiveExpense: activities.reduce((sum, activity) => sum + (activity.effectiveExpense || 0), 0),
            totalAccruedBalance: activities.reduce((sum, activity) => sum + (activity.accruedBalance || 0), 0),
            totalEffectiveBalance: activities.reduce((sum, activity) => sum + (activity.effectiveBalance || 0), 0)
        };
    }

    const complianceData = useMemo(() => {
        if (userData?.dimension === 'Sostenedor') {
            // Obtener establecimientos del sostenedor
            const holderEstablishments = establishmentList?.filter(establishment =>
                establishment.projectId === userData.tenantId
            ) || [];

            return holderEstablishments.map(establishment => {
                // Obtener PMEs del establecimiento
                const establishmentPmes = pmeList?.filter(pme =>
                    pme.establishmentId === establishment.id
                ) || [];

                // Obtener años únicos y ordenarlos descendentemente
                const uniqueYears = [...new Set(establishmentPmes.map(pme => pme.yearPME))]
                    .sort((a, b) => b - a)
                    .slice(0, 2); // Tomar solo los 2 años más recientes

                // Calcular cumplimiento para cada año
                const yearlyCompliance = uniqueYears.map(year => {
                    const pmesForYear = establishmentPmes.filter(pme => pme.yearPME === year);
                    const allActivities = pmesForYear.flatMap(pme => pme.activities || []);
                    const totals = calculateTotals(allActivities);

                    const percentage = totals.totalSubsidyAmount > 0
                        ? (totals.totalAccruedBalance * 100) / totals.totalSubsidyAmount
                        : 0;

                    return {
                        year,
                        compliance: percentage
                    };
                });

                return {
                    name: establishment.nameEstablishment,
                    rbd: establishment.rbd,
                    yearlyCompliance
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
        } else if (userData?.dimension === 'Establecimiento') {
            const establishmentPmes = pmeList?.filter(pme =>
                pme.establishmentId === userData.establishmentId
            ) || [];

            return establishmentPmes.map(pme => {
                // Calcular totales para cada PME
                const totals = calculateTotals(pme.activities || []);

                // Calcular el porcentaje de cumplimiento usando la misma fórmula
                const compliance = totals.totalSubsidyAmount > 0
                    ? (totals.totalAccruedBalance * 100) / totals.totalSubsidyAmount
                    : 0;

                return {
                    name: `PME ${pme.yearPME}`,
                    yearPME: pme.yearPME,
                    compliance: compliance
                };
            })
                .sort((a, b) => b.yearPME - a.yearPME);
        }

        return [];
    }, [pmeList, establishmentList, userData]);

    if (!complianceData.length) {
        return (
            <section>
                <h1>PME Compliance Summary</h1>
                <p>No existen datos de cumplimiento</p>
            </section>
        );
    }
    return (
        <>
            <h2>{userData?.dimension === 'Sostenedor'
                ? 'Cumplimiento de PME por Establecimiento'
                : 'Cumplimiento de PME por Año'}
            </h2>
            <article className='compliance-data flex'>
            {userData?.dimension === 'Sostenedor' ? (
                // Vista para Sostenedor
                complianceData.map((establishment, index) => (
                        <span key={index} className='compliance-card holder flex'>
                            <p>{establishment.name}</p>
                            <p>RBD: {establishment.rbd}</p>
                            {establishment.yearlyCompliance.length > 0 ? (
                                <div className='yearly-compliance flex'>
                                    {establishment.yearlyCompliance.map(({ year, compliance }) => (
                                        <span className='flex' key={year}>
                                            <p>PME {year}</p>
                                            <span className='card flex'>
                                                <ComplianceIndicator 
                                                    percentage={parseFloat(compliance.toFixed(1))} 
                                                />
                                            </span>
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <div className='no-pme flex'>
                                    <p>No tiene PME aún creados</p>
                                </div>
                            )}
                        </span>
                    ))
            ) : (
                // Vista para Establecimiento
                complianceData.map((item, index) => (
                    <span key={index} className='compliance-card flex'>
                        <p>{item.name}</p>
                        <span className='card flex'>
                            <ComplianceIndicator 
                                percentage={parseFloat(item.compliance.toFixed(1))} 
                            />
                        </span>
                    </span>
                ))
            )}
            </article>
        </>
    );
};

export default PMEComplianceSummary;